import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate, type ErrorComponentProps } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import TitleRow from '@/components/ui/TitleRow'
import { tierName } from '@/features/account/plan'
import { PLAN_PRESENTATION } from '@/features/onboarding/plans'
import { useStartCheckout } from '@/features/payments/api/mutations'
import { gatewayName } from '@/features/payments/lib/gateways'
import { OFFLINE_MESSAGE } from '@/features/payments/lib/payFailure'
import { useDiscountPreview } from '@/features/subscriptions/api/mutations'
import {
  planQueryOptions,
  useSuspensePlans,
  useSuspenseSubscriptionMe,
} from '@/features/subscriptions/api/queries'
import { subscriptionPayment } from '@/features/subscriptions/api/subscriptionPayment'
import { planSummary } from '@/features/subscriptions/lib/features'
import { useOnline } from '@/hooks/useOnline'
import { sign } from '@/lib/money'
import { GATEWAYS, type Gateway } from '@/schemas/payments'
import type { DiscountPreview, Entitlement } from '@/schemas/subscriptions'
import CadenceOptions from '@/screens/account/components/checkout/CadenceOptions'
import CheckoutPending from '@/screens/account/components/checkout/CheckoutPending'
import CheckoutPlanCard from '@/screens/account/components/checkout/CheckoutPlanCard'
import CheckoutSummary from '@/screens/account/components/checkout/CheckoutSummary'
import CheckoutUnavailable from '@/screens/account/components/checkout/CheckoutUnavailable'
import {
  CHECKOUT_COLUMNS,
  OPTIONS_COLUMN,
  OPTIONS_PAIR,
} from '@/screens/account/components/checkout/checkoutLayout'
import {
  checkoutSummary,
  codeFailureMessage,
  CODE_COVERS_ALL_MESSAGE,
  pastedCode,
  payFailure,
  resolveCheckoutPlan,
  rupees,
  type CheckoutPlan,
} from '@/screens/account/components/checkout/checkoutModel'
import DiscountCodeField from '@/screens/account/components/checkout/DiscountCodeField'
import GatewayOptions from '@/screens/account/components/checkout/GatewayOptions'
import SubscriptionSteps from '@/screens/account/components/SubscriptionSteps'
import { loadCheckout } from '@/screens/account/CheckoutScreen.loader'
import { checkoutSearchSchema, type CheckoutSearch } from '@/screens/account/subscription-search'

/** Back goes to the plans — the step before — not the Account hub. */
const HEADER = <TitleRow title="Checkout" back="/subscription" />

/**
 * How long the page may still be here after a hand-off before Pay comes back.
 * A gateway normally answers the form POST, and the page unloads, well within
 * it; still being here means the navigation was stopped or dropped. Generous
 * on purpose: a Pay pressed while a slow gateway is still answering starts a
 * second attempt (a second Pending row, though never a second charge).
 */
const HANDOFF_STALL_MS = 10_000

function CheckoutError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Couldn't load checkout" />
}

export const Route = createFileRoute('/_app/subscription/checkout')({
  validateSearch: checkoutSearchSchema,
  loader: loadCheckout,
  pendingComponent: CheckoutPending,
  errorComponent: CheckoutError,
  component: CheckoutScreen,
})

/** The URL the tab was opened with — see `pastedCode`. */
function openingUrl(): string | undefined {
  try {
    return performance.getEntriesByType('navigation')[0]?.name
  } catch {
    return undefined
  }
}

/**
 * Checkout — pay, step 2 of 3. V2 node `4243:76407`.
 *
 * `?plan=` is the whole selection: "How often" and the AI-signals choice
 * replace it (no new history entry), so a refresh or a shared link lands on
 * the same plan. `?gateway=` follows the gateway picked; `?code=` is written
 * only once mm-be's preview has accepted it for this plan.
 *
 * Pay POSTs `checkout/`, stashes the attempt and hands the browser to the
 * gateway (`useStartCheckout`). Deliberately not drawn from Figma: the
 * "Demo checkout" banner (payments are real), the Card gateway (mm-be has
 * none) and "VAT 13%" (VAT is the API's figure, drawn only above zero).
 */
function CheckoutScreen() {
  const search = Route.useSearch()
  const { data: plans } = useSuspensePlans()
  const { data: me } = useSuspenseSubscriptionMe()
  const [refused, setRefused] = useState(false)
  const checkout = useMemo(() => resolveCheckoutPlan(plans, search.plan), [plans, search.plan])

  return (
    <ScreenLayout header={HEADER} fullWidth>
      <SubscriptionSteps current="pay" />
      {refused ? (
        <CheckoutUnavailable reason="refused" />
      ) : checkout.kind !== 'ready' ? (
        <CheckoutUnavailable reason={checkout.kind} />
      ) : (
        <CheckoutForm
          checkout={checkout}
          me={me}
          search={search}
          onRefused={() => {
            setRefused(true)
          }}
        />
      )}
    </ScreenLayout>
  )
}

/** The member's paid plan as checkout names a plan, or null on Free. */
function currentPlanName(me: Entitlement): string | null {
  const tier = tierName(me.tier)
  if (tier === 'Free' || me.activeUntil === null) return null
  return me.features['signals']?.enabled === true ? `${tier} with AI signals` : tier
}

interface AppliedCode {
  code: string
  preview: DiscountPreview
}

function CheckoutForm({
  checkout,
  me,
  search,
  onRefused,
}: {
  checkout: Extract<CheckoutPlan, { kind: 'ready' }>
  me: Entitlement
  search: CheckoutSearch
  onRefused: () => void
}) {
  const { plan, tier } = checkout
  const presentation = PLAN_PRESENTATION[tier]
  const navigate = useNavigate({ from: Route.fullPath })
  const online = useOnline()

  const detail = useQuery({ ...planQueryOptions(plan.id), placeholderData: keepPreviousData })
  const flags = detail.data?.features
  const tagline = detail.isPending ? undefined : flags === undefined ? null : planSummary(flags)

  const [gateway, setGateway] = useState<Gateway>(search.gateway ?? 'esewa')
  const [unavailable, setUnavailable] = useState<ReadonlySet<Gateway>>(() => new Set())
  const [gatewayError, setGatewayError] = useState<string | null>(null)

  const [initialCode] = useState(() => pastedCode(search.code, openingUrl()))
  const [codeText, setCodeText] = useState(initialCode ?? '')
  // What the box says right now, for a check that comes back after it changed.
  const typedCode = useRef(initialCode ?? '')
  const [applied, setApplied] = useState<AppliedCode | null>(null)
  const [codeError, setCodeError] = useState<string | null>(null)

  const [payError, setPayError] = useState<string | null>(null)
  const [stalled, setStalled] = useState(false)

  const preview = useDiscountPreview()
  const start = useStartCheckout(subscriptionPayment)
  const { mutate: previewCode } = preview
  const { isSuccess: handedOff, reset: resetStart } = start
  const leaving = start.isPending || handedOff

  const writeCode = useCallback(
    (code: string | undefined) => {
      void navigate({ search: (prev) => ({ ...prev, code }), replace: true })
    },
    [navigate],
  )

  const applyCode = useCallback(
    (raw: string, planId: number) => {
      const code = raw.trim()
      if (code === '') return
      setCodeError(null)
      if (!navigator.onLine) {
        setCodeError(OFFLINE_MESSAGE)
        return
      }
      previewCode(
        { planId, code },
        {
          onSuccess: (result) => {
            if (typedCode.current.trim() !== code) return
            // mm-be previews a code that takes off everything, then refuses
            // it at checkout — say so now rather than at Pay.
            if (sign(result.discount.payable) <= 0) {
              setApplied(null)
              setCodeError(CODE_COVERS_ALL_MESSAGE)
              writeCode(undefined)
              return
            }
            setApplied({ code, preview: result })
            writeCode(code)
          },
          onError: (error) => {
            if (typedCode.current.trim() !== code) return
            setApplied(null)
            setCodeError(codeFailureMessage(error, navigator.onLine))
            writeCode(undefined)
          },
        },
      )
    },
    [previewCode, writeCode],
  )

  // A code that came in the link is checked once, as if typed and applied.
  // Ref-guarded: StrictMode runs this twice, and each check counts against
  // mm-be's 30-an-hour limit.
  const checkedPasted = useRef(false)
  useEffect(() => {
    if (checkedPasted.current || initialCode === undefined) return
    checkedPasted.current = true
    applyCode(initialCode, plan.id)
  }, [applyCode, initialCode, plan.id])

  // A hand-off that never unloaded the page gives Pay back.
  useEffect(() => {
    if (!handedOff) return
    const timer = window.setTimeout(() => {
      setStalled(true)
      resetStart()
    }, HANDOFF_STALL_MS)
    return () => {
      window.clearTimeout(timer)
    }
  }, [handedOff, resetStart])

  const pickPlan = (planId: number) => {
    if (planId === plan.id) return
    setPayError(null)
    void navigate({ search: (prev) => ({ ...prev, plan: planId }), replace: true })
    // A code is checked against one plan; the next plan gets its own check.
    const code = applied?.code ?? (preview.isPending ? preview.variables.code : undefined)
    if (code !== undefined) {
      setApplied(null)
      applyCode(code, planId)
    }
  }

  const pickGateway = (next: Gateway) => {
    setGateway(next)
    setGatewayError(null)
    setPayError(null)
    setCodeError(null)
    void navigate({ search: (prev) => ({ ...prev, gateway: next }), replace: true })
  }

  const removeCode = () => {
    setApplied(null)
    setCodeText('')
    typedCode.current = ''
    setCodeError(null)
    writeCode(undefined)
  }

  const discount = applied?.preview.planId === plan.id ? applied : null
  const summary = checkoutSummary(plan, presentation.name, discount?.preview ?? null)
  const total = rupees(summary.total)

  const pay = () => {
    setPayError(null)
    setGatewayError(null)
    setCodeError(null)
    setStalled(false)
    if (!online) {
      setPayError(OFFLINE_MESSAGE)
      return
    }
    start.mutate(
      { planId: plan.id, gateway, code: discount?.code },
      {
        onError: (error) => {
          const failure = payFailure(error, navigator.onLine, discount !== null)
          switch (failure.kind) {
            case 'gateway': {
              // The pick moves to a gateway that can still be paid with, so Pay
              // stays usable; the failed one stays listed, disabled.
              const off = new Set(unavailable).add(gateway)
              const next = GATEWAYS.find((candidate) => !off.has(candidate))
              setUnavailable(off)
              if (next !== undefined) {
                setGateway(next)
                void navigate({ search: (prev) => ({ ...prev, gateway: next }), replace: true })
              }
              setGatewayError(
                next === undefined
                  ? `${gatewayName(gateway)} isn't available right now.`
                  : `${gatewayName(gateway)} isn't available right now. We've picked ${gatewayName(next)} instead.`,
              )
              return
            }
            case 'plan':
              onRefused()
              return
            case 'code':
              setApplied(null)
              setCodeError(failure.text)
              writeCode(undefined)
              return
            case 'message':
              setPayError(failure.text)
          }
        },
      },
    )
  }

  return (
    <div className={CHECKOUT_COLUMNS}>
      <div className={OPTIONS_COLUMN}>
        <CheckoutPlanCard
          presentation={presentation}
          plan={plan}
          tagline={tagline}
          replaces={currentPlanName(me)}
        />
        <div className={OPTIONS_PAIR}>
          <CadenceOptions checkout={checkout} disabled={leaving} onPick={pickPlan} />
          <GatewayOptions
            value={gateway}
            onChange={pickGateway}
            unavailable={unavailable}
            error={gatewayError}
            disabled={leaving}
          />
        </div>
        <DiscountCodeField
          value={codeText}
          onChange={(value) => {
            setCodeText(value)
            typedCode.current = value
            setCodeError(null)
          }}
          applied={
            discount === null
              ? null
              : { code: discount.code, amountOff: discount.preview.discount.amountOff }
          }
          error={codeError}
          checking={preview.isPending}
          disabled={leaving}
          onApply={() => {
            applyCode(codeText, plan.id)
          }}
          onRemove={removeCode}
        />
      </div>
      <CheckoutSummary
        rows={summary.rows}
        total={total}
        gatewayName={gatewayName(gateway)}
        leaving={leaving}
        blocked={preview.isPending || unavailable.has(gateway)}
        error={payError}
        stalled={stalled}
        onPay={pay}
      />
    </div>
  )
}
